import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import type { Domain } from '@feathr/blackbox';
import { Button, Layout, Toolbar } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useFlags, useLocalUrl, useStore } from '@feathr/extender/state';

import { DOMAIN_SETUP_ARTICLE_URL } from './constants';
import DomainSetupModal from './DomainSetupModal';
import DomainsTable from './DomainsTable';

function DomainsPage(): JSX.Element {
  const { t } = useTranslation();
  const { Domains } = useStore();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { entriDomainAuth } = useFlags();
  const [setupDomain, setSetupDomain] = useState<Domain | undefined>(undefined);
  const [isSetupOpen, setIsSetupOpen] = useState(false);

  function handleAdd(): void {
    const domain = Domains.create();
    if (entriDomainAuth) {
      setSetupDomain(domain);
      setIsSetupOpen(true);
      return;
    }
    history.push(localUrl(domain.getItemUrl('edit')));
  }

  function handleCloseSetup(): void {
    setIsSetupOpen(false);
  }

  return (
    <Page
      actions={
        <Toolbar>
          <Button onClick={handleAdd} prefix={<FontAwesomeIcon icon={faPlus} />} type={'primary'}>
            {t('Add new domain')}
          </Button>
        </Toolbar>
      }
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={
        <Layout width={'max'}>
          <Trans t={t}>
            <p>
              Set up and manage domains to use for sending emails, and serving landing pages and
              invites campaign collateral. Learn more about{' '}
              <a href={DOMAIN_SETUP_ARTICLE_URL} target={'_blank'}>
                content domains and email domains
              </a>
              , and how they can be used.
            </p>
            <p>
              We highly recommend also{' '}
              <a href={'https://dmarc.org'} target={'_blank'}>
                configuring the DMARC for your domain
              </a>
              .
            </p>
          </Trans>
        </Layout>
      }
      title={t('Domains')}
    >
      <DomainsTable />
      {setupDomain && (
        <DomainSetupModal
          domain={setupDomain}
          key={setupDomain.id}
          onClose={handleCloseSetup}
          opened={isSetupOpen}
        />
      )}
    </Page>
  );
}

export default observer(DomainsPage);
